import { Modification } from '@core/types';
import { ModificationRegistry } from '@shared/modifications';

export class PreviewManager {
  private isPreviewEnabled = true;
  private previewElements: Map<string, Element[]> = new Map();
  private originalStates: Map<
    string,
    {
      text: string;
      display: string;
      visibility: string;
      src?: string;
      backgroundImage?: string;
      innerHTML?: string;
    }[]
  > = new Map();
  private modificationRegistry = new ModificationRegistry();
  private currentModifications: Modification[] = [];

  togglePreview(): boolean {
    this.isPreviewEnabled = !this.isPreviewEnabled;
    return this.isPreviewEnabled;
  }

  setPreviewEnabled(enabled: boolean, modifications: Modification[] = []): void {
    if (enabled) {
      this.applyPreview(modifications);
    } else {
      this.clearPreview();
    }
    this.isPreviewEnabled = enabled;
  }

  applyPreview(modifications: Modification[]): void {
    this.currentModifications = modifications;

    modifications.forEach((mod) => {
      try {
        const elements = document.querySelectorAll(mod.selector);
        const states: {
          text: string;
          display: string;
          visibility: string;
          src?: string;
          backgroundImage?: string;
        }[] = [];

        elements.forEach((element) => {
          // Capture original state before applying modification
          const state: {
            text: string;
            display: string;
            visibility: string;
            src?: string;
            backgroundImage?: string;
            innerHTML?: string;
          } = {
            text: (element as HTMLElement).textContent || '',
            display: (element as HTMLElement).style.display || '',
            visibility: (element as HTMLElement).style.visibility || '',
          };

          // Capture image-specific state
          if (element instanceof HTMLImageElement) {
            state.src = element.src || '';
          } else {
            // Capture background image for other elements
            const computedStyle = window.getComputedStyle(element);
            state.backgroundImage = computedStyle.backgroundImage || '';
          }

          // Capture innerHTML for text modifications (to restore original structure)
          if (mod.action === 'set_text') {
            state.innerHTML = (element as HTMLElement).innerHTML;
          } else if (mod.action === 'hide') {
            // Also capture innerHTML for hidden elements to restore full structure
            state.innerHTML = (element as HTMLElement).innerHTML;
          }

          states.push(state);

          try {
            this.modificationRegistry.apply(mod, element);
          } catch (error) {
            console.warn('Failed to apply modification to element', { mod, error });
          }
        });

        this.originalStates.set(mod.selector, states);
        this.previewElements.set(mod.selector, Array.from(elements));
      } catch (error) {
        console.warn('Preview error for selector:', mod.selector, error);
      }
    });
  }

  clearPreview(): void {
    this.originalStates.forEach((states, selector) => {
      const elements = this.previewElements.get(selector) || [];
      const modification = this.findModificationForSelector(selector);

      elements.forEach((element, index) => {
        const state = states[index];
        if (state && modification) {
          // Use handler's remove method to clean up and restore
          try {
            const handler = this.modificationRegistry.getHandler(modification.action);
            if (handler) {
              handler.remove(element);
            }

            // For text modifications, also restore innerHTML since handler only handles observers
            if (modification.action === 'set_text' && state.innerHTML !== undefined) {
              (element as HTMLElement).innerHTML = state.innerHTML;
            }
          } catch (error) {
            console.warn('Failed to restore element:', error);
            // Fallback to manual restoration
            this.restoreStateManually(element, state);
          }
        }
      });
    });

    this.originalStates.clear();
    this.previewElements.clear();
    this.currentModifications = [];
  }

  private findModificationForSelector(selector: string): Modification | undefined {
    return this.currentModifications.find((mod) => mod.selector === selector);
  }

  private restoreStateManually(element: Element, state: any): void {
    // Restore text content - use innerHTML if available (for text modifications)
    if (state.innerHTML !== undefined) {
      (element as HTMLElement).innerHTML = state.innerHTML;
    } else {
      (element as HTMLElement).textContent = state.text;
    }

    // For visibility: check if handler stored original display
    if ((element as any).dataset._origDisplay) {
      (element as HTMLElement).style.display = (element as any).dataset._origDisplay;
      delete (element as any).dataset._origDisplay;
    } else {
      (element as HTMLElement).style.display = state.display;
    }

    (element as HTMLElement).style.visibility = state.visibility;

    // Restore image-specific properties
    if (element instanceof HTMLImageElement && state.src) {
      element.src = state.src;
    } else if (state.backgroundImage) {
      (element as HTMLElement).style.backgroundImage = state.backgroundImage;
      // Also restore other background properties that might have been changed
      (element as HTMLElement).style.backgroundRepeat = '';
      (element as HTMLElement).style.backgroundPosition = '';
      (element as HTMLElement).style.backgroundSize = '';
    }

    // Clean up any tracking attributes
    delete (element as any).dataset.campaignImageModified;
    delete (element as any).dataset.campaignImageUrl;
    delete (element as any).dataset.originalSrc;
    delete (element as any).dataset.originalAlt;
    delete (element as any).dataset.originalBackground;
  }

  isEnabled(): boolean {
    return this.isPreviewEnabled;
  }
}
